import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../gen/api/authentication.service';
import { AuthenticationFlowService } from '../gen/api/authenticationFlow.service';
import { Logger } from '../util/logger';
import ResponseUtil from '../util/responseUtil';
import { UserService } from '../gen/api/user.service';
import { SessionService } from '../gen';
import AuthenticationUtils from '../util/AuthenticationUtils';
import { ParamsService } from './params.service';
import { AppConstants } from './AppConstants';
import * as i0 from "@angular/core";
import * as i1 from "../util/logger";
const cloneDeep = require('lodash.clonedeep');
export class AuthenticationInfoService {
    constructor(authenticationApi, authenticationFlowApi, userApi, sessionApi, logger) {
        this.authenticationApi = authenticationApi;
        this.authenticationFlowApi = authenticationFlowApi;
        this.userApi = userApi;
        this.sessionApi = sessionApi;
        this.logger = logger;
        this.profileSubscription = new BehaviorSubject(undefined);
        this.skippedInvitations = [];
        this.authenticationInfo = new BehaviorSubject(undefined);
        // Todo: rename uistate
        this.sessionInfo = new BehaviorSubject({
            hasSession: false,
            fullyAuthenticated: false,
            tasksInProgress: [],
            optionalsHandled: false,
        });
    }
    startTask(taskId) {
        this.logger.debug('started: %s', taskId);
        const newInfo = cloneDeep(this.sessionInfo.value);
        newInfo.tasksInProgress.push(taskId);
        this.sessionInfo.next(newInfo);
    }
    completeTask(taskId) {
        this.logger.debug('completed: %s', taskId);
        const newInfo = cloneDeep(this.sessionInfo.value);
        if (newInfo.tasksInProgress.indexOf(taskId) !== newInfo.tasksInProgress.length - 1) {
            throw new Error('AuthenticationInfoService.completeTask: Task not in progress');
        }
        else {
            newInfo.tasksInProgress.pop();
            this.sessionInfo.next(newInfo);
        }
    }
    cancelTask(taskId) {
        this.logger.debug('cancelled: %s', taskId);
        const newInfo = cloneDeep(this.sessionInfo.value);
        if (newInfo.tasksInProgress.indexOf(taskId) !== newInfo.tasksInProgress.length - 1) {
            throw new Error('AuthenticationInfoService.cancelTask: Task not in progress');
        }
        else {
            newInfo.tasksInProgress.pop();
            this.sessionInfo.next(newInfo);
        }
    }
    skipInvitation(id) {
        if (this.skippedInvitations.indexOf(id) < 0) {
            this.skippedInvitations.push(id);
            this.fetchAuthenticationInfo();
        }
    }
    clearSkippedInvitations() {
        if (this.skippedInvitations.length) {
            this.skippedInvitations = [];
        }
    }
    getUser() {
        return new Observable(subscriber => {
            const t = this.userApi.getUser('response');
            t.subscribe((resp) => {
                subscriber.next(resp);
                if (resp && resp.body) {
                    this.profileSubscription.next(resp.body);
                }
                else {
                    this.profileSubscription.next(null);
                }
            }, (err) => {
                subscriber.error(err);
                this.profileSubscription.next(null);
            });
        });
    }
    getProfileSubscription() {
        return this.profileSubscription;
    }
    updateUser(body) {
        return this.userApi.updateUser(body, 'response');
    }
    registerUser(body, verifyEmail, verifyEmailUri, remember) {
        return new Observable(subscriber => {
            const t = this.userApi.registerUser(body, verifyEmail, verifyEmailUri, remember, 'response');
            t.subscribe(resp => {
                subscriber.next(resp);
                this.fetchAuthenticationInfo();
            }, err => {
                subscriber.error(err);
                this.fetchAuthenticationInfo();
            });
        });
    }
    endSession(ignoreConsumerSingleSignOut, ignoreProviderSingleSignOut) {
        return new Observable(subscriber => {
            const t = this.sessionApi.endSession(ignoreConsumerSingleSignOut, ignoreProviderSingleSignOut, 'response');
            t.subscribe(response => {
                subscriber.next(response);
                this.clearSkippedInvitations();
                this.fetchAuthenticationInfo();
            }, err => {
                subscriber.error(err);
                this.clearSkippedInvitations();
                this.fetchAuthenticationInfo();
            });
        });
    }
    addAuthentications(credentials, remember) {
        return new Observable(subscriber => {
            const t = this.authenticationApi.addAuthentications(credentials, remember, 'response');
            t.subscribe(response => {
                this.fetchAuthenticationInfo(null, () => {
                    subscriber.next(response);
                });
            }, err => {
                subscriber.error(err);
                this.fetchAuthenticationInfo();
            });
        });
    }
    fetchAuthenticationInfo(username, callback) {
        const listExistingAuthenticationsRequest = this.authenticationApi.listAuthentications('response')
            .pipe(catchError(error => ResponseUtil.handleAuthenticationChallengeResponse(error)));
        const listUserAuthenticationFlowsRequest = this.authenticationFlowApi.listUserAuthenticationFlows(username || '', 'response');
        const bundledRequests = forkJoin([listExistingAuthenticationsRequest, listUserAuthenticationFlowsRequest]);
        bundledRequests.subscribe(responses => {
            // first handle the listUserAuthenticationFlowsRequest
            const listUserAuthenticationFlowsResponse = responses[1];
            const userAuthenticationFlows = listUserAuthenticationFlowsResponse.body;
            this.logger.debug('Users authentication flows: %O', userAuthenticationFlows || []);
            // second handle the listExistingAuthenticationsRequest
            const listExistingAuthenticationsResponse = responses[0];
            let existingAuthenticationObjs = null;
            if (listExistingAuthenticationsResponse instanceof HttpResponse) {
                existingAuthenticationObjs = listExistingAuthenticationsResponse.body;
            }
            else if (listExistingAuthenticationsResponse instanceof HttpErrorResponse) {
                existingAuthenticationObjs = listExistingAuthenticationsResponse.error;
            }
            else {
                existingAuthenticationObjs = [];
            }
            this.logger.debug('Existing authentications: %O', existingAuthenticationObjs);
            let challenges = null;
            const additionalRequirement = ParamsService.getParamSync(AppConstants.QP_REQUIRE_CHALLENGE);
            let additionalChallenge = null;
            if (existingAuthenticationObjs && existingAuthenticationObjs.length) {
                const nextChallenge = AuthenticationUtils.findNextChallenge({
                    authenticationFlows: userAuthenticationFlows,
                    existingAuthentications: existingAuthenticationObjs,
                    requiredAuthentications: null,
                    optionalAuthentications: null,
                });
                const req = [];
                const opt = [];
                if (additionalRequirement === AppConstants.AC_AM_INVITATION_ACCEPTED) {
                    additionalChallenge = {
                        token: ParamsService.getParamSync(AppConstants.QP_INVITATION_TOKEN),
                        email: ParamsService.getParamSync(AppConstants.QP_EMAIL),
                        authenticationMethod: AppConstants.AC_AM_INVITATION_ACCEPTED,
                    };
                }
                else if (additionalRequirement) {
                    throw new Error('AuthenticationInforService.fetchAuthenticationInfo: Unsupported additional requirement ' +
                        additionalRequirement);
                }
                let additionalRequirementAdded = false;
                if (nextChallenge) {
                    let chall = nextChallenge;
                    while (chall) {
                        if (additionalRequirement === AppConstants.AC_AM_INVITATION_ACCEPTED &&
                            chall.authenticationMethod === AppConstants.AC_AM_INVITATION) {
                            req.push(additionalChallenge);
                            additionalRequirementAdded = true;
                        }
                        if (chall.authenticationMethod !== AppConstants.AC_AM_INVITATION || (this.skippedInvitations.indexOf(chall.invitationId) < 0 && chall.verified)) {
                            if (chall.required) {
                                req.push(chall);
                            }
                            else {
                                opt.push(chall);
                            }
                        }
                        //
                        chall = chall.challenges ? chall.challenges[0] : null;
                    }
                    if (!additionalRequirementAdded && additionalRequirement) {
                        req.push(additionalChallenge);
                    }
                }
                else if (additionalRequirement) {
                    req.push(additionalChallenge);
                }
                challenges = {
                    optional: opt,
                    required: req,
                };
            }
            else {
                challenges = {
                    required: [],
                    optional: [],
                };
            }
            this.logger.debug('Required authentications: %O', challenges.required || []);
            this.logger.debug('Optional authentications: %O', challenges.optional || []);
            const authnInfo = {
                authenticationFlows: userAuthenticationFlows,
                existingAuthentications: existingAuthenticationObjs,
                requiredAuthentications: challenges.required,
                optionalAuthentications: challenges.optional,
            };
            const sessInfo = {
                hasSession: existingAuthenticationObjs.length > 0,
                fullyAuthenticated: existingAuthenticationObjs.length > 0 && (!challenges.required || challenges.required.length === 0),
                tasksInProgress: [].concat(this.sessionInfo.value.tasksInProgress),
                optionalsHandled: challenges.optional.length === 0,
            };
            this.logger.debug('Users authentication status:\n - hasSession: %O \n - fullyAuthenticated: %O \n - optionalsHandled: %O', sessInfo.hasSession, sessInfo.fullyAuthenticated, sessInfo.optionalsHandled);
            this.authenticationInfo.next(authnInfo);
            this.sessionInfo.next(sessInfo);
            if (callback) {
                callback(authnInfo, sessInfo);
            }
        });
    }
}
AuthenticationInfoService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function AuthenticationInfoService_Factory() { return new AuthenticationInfoService(i0.ɵɵinject("AuthenticationApi"), i0.ɵɵinject("AuthenticationFlowApi"), i0.ɵɵinject("UserApi"), i0.ɵɵinject("SessionApi"), i0.ɵɵinject(i1.Logger)); }, token: AuthenticationInfoService, providedIn: "root" });
